// Shared branding inputs for the combined-workspace PDF, used by both the
// Combined PDF preview popup and the confirm-page "Finish" action so the two
// entry points generate an identically branded PDF.

// Each tenant types its translate function to its own key union, and the keys
// read through it are optional per tenant, so the key type stays loose on purpose.
export type TranslateFn = (key: any) => string;

// i18next returns the key itself when a variable is unset, so a value equal to
// its key means "not configured for this tenant".
export const readText = (t: TranslateFn, key: string): string | undefined => {
  const value = t(key);
  return value && value !== key ? value : undefined;
};

// The PDF colour is configured per collection and is independent of the colour
// the configurator runs on: a distributor repainting the builder (css
// color-accent -> --color-primary) no longer repaints the PDF with it. With
// nothing configured the PDF falls back to the tenant build's own brand colour,
// which --color-brand-base holds precisely because a collection cannot override
// it; the backend defaults to blue if even that is absent.
export const readBrandAccent = (t: TranslateFn): string | undefined => {
  const configured = readText(t, 'variables.pdf.accentColor');
  if (configured) return configured;

  const base = getComputedStyle(document.documentElement).getPropertyValue('--color-brand-base').trim();
  return base || undefined;
};

// Distributor logo from the tenant/rule-set i18n (the same variable the legacy
// PDF used). i18next returns the key itself when the variable is unset; resolve
// a relative asset path to an absolute URL so Gotenberg can fetch it.
export const resolveLogoUrl = (raw: string): string | undefined => {
  if (!raw || raw === 'variables.pdf.logo') return undefined;
  if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
  return `${window.location.origin}${raw}`;
};
